import { useEffect, useState } from 'react';
import { TabProps } from '../../../../blocks/tabs-vertical/TabsVertical';
import Button from '../../../../components/button/Button';

import { Container, Row, SectionContent } from '../../../../styling/FormStyling';
import styled from 'styled-components';
import Section from '../../../../components/section/Section';
import CircularProgress from '../../../../components/circular-progress/CircularProgress';
import TaskService from '../../../../services/TaskService';
import NotificationService from '../../../../services/NotificationService';
import { log } from '../../../../utils/logging/log';
import SimpleTime from '../../../../components/simple-time/SimpleTime';
import { convertHoursAndMinutesToMinutes, convertMinutesToHoursAndMinutes } from '../../../../utils/timeHandling';
import DatePicker from '../../../../components/date-picker/DatePicker';
import { changeToEndOfDay, formatDateString } from '../../../../utils/dateHandling';
import TextRow from '../components/TextRow';
import Checkbox from '../../../../components/checkbox/Checkbox';
import { getInitialsFromEmail } from '../../../../utils/initialsUtils';
import { RevisitDTO, WorkTaskStatus } from '../../../../api/api';
import Typography from '../../../../components/typography/Typography';
import { useSelector } from 'react-redux';
import { selectUserProfile } from '../../../../stateManagement/reducers/userProfileReducer';

interface Props extends TabProps {
  workTaskId: number;
  previousTechnicianName: string;
  previousTechnicianEmail: string;
  revisit?: RevisitDTO;
  fetchTask: () => void;
  taskStatus: WorkTaskStatus;
}

const RevisitStep = (props: Props) => {
  const { workTaskId, previousTechnicianEmail, previousTechnicianName, revisit, fetchTask, taskStatus } = props;

  const [loading, setLoading] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [normTime, setNormTime] = useState(revisit?.remainingTimeMin ?? 30);
  const [description, setDescription] = useState(revisit?.description ?? '');
  const [sameTechnician, setSameTechnician] = useState(false);
  const [technician, setTechnician] = useState(revisit?.requestedTechnician ?? '');
  const [startDate, setStartDate] = useState(revisit?.startDate ?? new Date().toISOString());
  const [endDate, setEndDate] = useState(revisit?.endDate ?? '');
  const [revisitCreated, setRevisitCreated] = useState(!!revisit);
  const [useForPlanning, setUseForPlanning] = useState(revisit?.useForPlanning);
  const userDepartment = useSelector(selectUserProfile).userProfile?.department;

  useEffect(() => {
    if (!startDate) return;
    const date = new Date(startDate);
    date.setDate(date.getDate() + 7);
    setEndDate(changeToEndOfDay(date).toISOString());
  }, [startDate]);

  const submit = () => {
    setHasSubmitted(true);

    const isStartDateValid = !!startDate;
    const isEndDateValid = !!endDate && new Date(endDate) >= new Date(startDate);
    const isNormTimeValid = normTime >= 30;
    const isDescriptionValid = !!description;
    const isTechnicianValid = !!technician;

    if (!(isNormTimeValid && isStartDateValid && isEndDateValid && isDescriptionValid && isTechnicianValid)) {
      return;
    }

    setLoading(true);

    const body = {
      workTaskId,
      description,
      remainingTimeMin: normTime,
      startDate,
      endDate,
      requestedTechnician: technician
    };

    const action = revisitCreated ? TaskService.updateRevisit : TaskService.createRevisit;
    action(body)
      .then((res: RevisitDTO | void) => {
        if (!revisitCreated) {
          setUseForPlanning((res as RevisitDTO).useForPlanning);
        }
        NotificationService.success('Genkørsel blev oprettet');
        setRevisitCreated(true);
        fetchTask();
      })
      .catch((error) => {
        log(error);
        NotificationService.error('Kunne ikke oprette genkørsel');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const setUseForPlanningHandler = (_useForPlanning: boolean) => {
    TaskService.updateRevisit({
      description,
      endDate,
      remainingTimeMin: normTime,
      requestedTechnician: technician,
      startDate,
      useForPlanning: _useForPlanning,
      workTaskId
    })
      .then(() => {
        setUseForPlanning(_useForPlanning);
        NotificationService.success('Anvend ved send til FLS blev opdateret');
      })
      .catch(() => {
        NotificationService.error('Der skete en fejl. ');
      });
  };

  const timeChangeHandler = (hours: number, minutes: number) => {
    setNormTime(convertHoursAndMinutesToMinutes(hours, minutes));
  };

  const handleStartDateChange = (date: string) => {
    setStartDate(date);
  };

  const handleDeadlineDateChange = (date: string) => {
    setEndDate(changeToEndOfDay(new Date(date)).toISOString());
  };

  const sameTechnicianCheckboxHandler = (checked: boolean) => {
    setSameTechnician(checked);
    if (checked && previousTechnicianName && previousTechnicianEmail) {
      setTechnician(`${previousTechnicianName} (${getInitialsFromEmail(previousTechnicianEmail)}) ønskes igen på opgaven.`);
    } else {
      setTechnician('');
    }
  };

  return (
    <Container>
      {loading ? (
        <CircularProgress />
      ) : (
        <Section header={'Genkørsel'} isExpanded>
          <SectionContent>
            <Row>
              <SimpleTime
                initialHours={convertMinutesToHoursAndMinutes(normTime).hours}
                initialMinutes={convertMinutesToHoursAndMinutes(normTime).minutes}
                onTimeChange={(hours, minutes) => timeChangeHandler(hours, minutes)}
                error={hasSubmitted && normTime < 30}
                helperText={hasSubmitted && normTime < 30 ? 'Minimum 30 minutter skal registeres' : ''}
              />
            </Row>
            <Row>
              <DatePicker
                onDateChanged={(date) => handleStartDateChange(date.toISOString())}
                value={startDate ? formatDateString(startDate) : ''}
                label="Startdato"
                required
                error={hasSubmitted && !startDate}
                helperText={hasSubmitted && !startDate ? 'Startdato er påkrævet' : ''}
              />
              <DatePicker
                onDateChanged={(date) => handleDeadlineDateChange(date.toISOString())}
                value={formatDateString(endDate)}
                label="Slutdato"
                required
                error={hasSubmitted && (!endDate || new Date(endDate) < new Date(startDate))}
                helperText={
                  hasSubmitted && (!endDate || new Date(endDate) < new Date(startDate))
                    ? 'Slutdato skal være efter startdato'
                    : ''
                }
              />
            </Row>
            <Row>
              <TextRow
                inputMode
                type="multilineText"
                label="Opgavebeskrivelse"
                value={description}
                onChange={(value) => setDescription(value)}
                error={hasSubmitted && !description}
                helperText={hasSubmitted && !description ? 'Beskrivelse er påkrævet' : ''}
              />
            </Row>
            <Row>
              <Column>
                {previousTechnicianName && previousTechnicianEmail && (
                  <Checkbox
                    label="Samme tekniker ønskes på opgaven"
                    checked={sameTechnician}
                    onChange={(e) => sameTechnicianCheckboxHandler(e.target.checked)}
                  />
                )}
                <TextRow
                  inputMode
                  type="multilineText"
                  label="Ønsket tekniker"
                  value={technician}
                  onChange={(value) => setTechnician(value)}
                  error={hasSubmitted && !technician}
                  helperText={hasSubmitted && !technician ? 'Ønsket tekniker skal udfyldes' : ''}
                />
              </Column>
            </Row>
            <Row>
              {
                <Button disabled={loading} onClick={submit} isLoading={loading}>
                  {`${revisitCreated ? 'Opdater' : 'Opret'} genkørsel`}
                </Button>
              }
              {taskStatus === WorkTaskStatus.Pause && userDepartment?.name === 'Planlægning' && (
                <Checkbox
                  label="Anvend ved send til FLS"
                  checked={useForPlanning}
                  onChange={(e) => setUseForPlanningHandler(e.target.checked)}
                />
              )}
            </Row>
          </SectionContent>
        </Section>
      )}
    </Container>
  );
};

export default RevisitStep;

const Column = styled.div<{ align?: 'flex-start' | 'flex-end' }>`
  align-content: ${(props) => props.align};
  width: 100%;

  & > div {
    margin-top: 20px;
  }
`;
